/**
 * Normalize /hotelImages (GenericImages.images) into deduped https URL strings.
 * Handles: string URLs, legacy { imageurl }, JSON string responses, comma-separated strings.
 * @param {unknown} raw
 * @returns {string[]}
 */
export function coerceGenericImagesToUrlList(raw) {
  if (raw == null) return [];
  if (typeof raw === "string") {
    const parts = raw
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    return parts.map((u) => normalizeOneUrl(u)).filter(Boolean);
  }
  if (!Array.isArray(raw)) return [];
  const out = [];
  for (const item of raw) {
    if (typeof item === "string") {
      const u = normalizeOneUrl(item.trim());
      if (u) out.push(u);
      continue;
    }
    if (item && typeof item === "object") {
      const u =
        item.imageurl ||
        item.imageUrl ||
        item.ImageUrl ||
        item.url ||
        item.Url ||
        item.FullSize ||
        item.href;
      if (typeof u === "string") {
        const nu = normalizeOneUrl(u.trim());
        if (nu) out.push(nu);
      }
    }
  }
  return dedupeUrls(out);
}

function normalizeOneUrl(u) {
  if (!u || typeof u !== "string") return null;
  const t = u.trim();
  if (!t) return null;
  if (t.startsWith("//")) return `https:${t}`;
  if (t.startsWith("http://") || t.startsWith("https://")) {
    return t.replace(
      /https?:\/\/(az712897\.vo\.msecnd\.net|az712897\.xyz)/g,
      "https://imagecontent.net"
    );
  }
  return null;
}

function dedupeUrls(urls) {
  const seen = new Set();
  const out = [];
  for (const u of urls) {
    if (!u || seen.has(u)) continue;
    seen.add(u);
    out.push(u);
  }
  return out;
}

/**
 * Parse axios response body that may be an object or a JSON string (legacy endpoints).
 * @param {unknown} data
 * @returns {Record<string, unknown>}
 */
export function parseHotelImagesResponseBody(data) {
  if (data && typeof data === "object" && !Array.isArray(data)) {
    return /** @type {Record<string, unknown>} */ (data);
  }
  if (typeof data === "string") {
    try {
      const o = JSON.parse(data);
      return o && typeof o === "object" && !Array.isArray(o) ? o : {};
    } catch {
      return {};
    }
  }
  return {};
}

const NO_IMAGE_PLACEHOLDERS = new Set([
  "אין תמונות למלון זה",
  "No images for this hotel",
]);

function isNoImagePlaceholder(value) {
  return typeof value === "string" && NO_IMAGE_PLACEHOLDERS.has(value.trim());
}

function normalizeProfileMediaUrl(value) {
  if (value == null) return null;
  if (typeof value === "string") {
    const t = value.trim();
    if (!t || isNoImagePlaceholder(t)) return null;
    const [one] = coerceGenericImagesToUrlList([t]);
    return one || null;
  }
  if (typeof value === "object") {
    const u =
      value.url ??
      value.src ??
      value.image ??
      value.imageUrl ??
      value.imageurl ??
      value.ImageUrl ??
      value.FullSize ??
      value.fullSize ??
      value.thumbnail ??
      value.href;
    return normalizeProfileMediaUrl(u);
  }
  return null;
}

function pushProfileImageUnique(bucket, url, source) {
  const nu = normalizeProfileMediaUrl(url);
  if (!nu) return;
  if (bucket.some((row) => row.url === nu)) return;
  bucket.push({ url: nu, source });
}

function urlsFromStructuredProfileImages(value, source) {
  const out = [];
  if (value == null) return out;
  if (Array.isArray(value)) {
    for (const item of value) {
      if (typeof item === "string") {
        pushProfileImageUnique(out, item, source);
        continue;
      }
      if (item && typeof item === "object") {
        const src =
          typeof item.source === "string" && item.source.trim()
            ? item.source
            : source;
        pushProfileImageUnique(
          out,
          item.url ?? item.resourceUrl ?? item.imageUrl ?? item.FullSize,
          src
        );
      }
    }
    return out;
  }
  if (typeof value === "object") {
    const nested = value.images ?? value.Images;
    if (nested != null) return urlsFromStructuredProfileImages(nested, source);
  }
  return out;
}

function collectFlatSupplierProfileUrls(rawHotel) {
  const ordered = [];
  const flatKeys = [
    "resourceUrl",
    "imageUrl",
    "hotelImage",
    "image",
    "mainImage",
    "thumbnailUrl",
    "getImage",
    "GetImage",
  ];
  for (const key of flatKeys) {
    pushProfileImageUnique(ordered, rawHotel[key], "supplier_profile");
  }
  for (let i = 1; i <= 12; i += 1) {
    pushProfileImageUnique(ordered, rawHotel[`galleryImage${i}`], "supplier_profile");
  }
  const gi = rawHotel.GenericImages ?? rawHotel.genericImages;
  if (gi && typeof gi === "object") {
    for (const u of coerceGenericImagesToUrlList(gi.images ?? gi.Images)) {
      pushProfileImageUnique(ordered, u, "supplier_profile");
    }
  }
  return ordered;
}

function collectSupplierProfileUrls(rawHotel) {
  const ordered = collectFlatSupplierProfileUrls(rawHotel);
  const boxes = rawHotel.boxes;
  if (Array.isArray(boxes)) {
    for (const box of boxes) {
      if (!box || typeof box !== "object") continue;
      const images = box.images;
      if (Array.isArray(images)) {
        for (const u of images) pushProfileImageUnique(ordered, u, "supplier_profile");
      }
    }
  }
  for (const key of ["Pictures", "pictures", "HotelImages", "hotelImages"]) {
    const block = rawHotel[key];
    if (!Array.isArray(block)) continue;
    for (const item of block) {
      if (typeof item === "string") {
        pushProfileImageUnique(ordered, item, "supplier_profile");
      } else if (item && typeof item === "object") {
        pushProfileImageUnique(
          ordered,
          item.url ?? item.Url ?? item.imageUrl ?? item.FullSize,
          "supplier_profile"
        );
      }
    }
  }
  for (const [key, source] of [
    ["hotelProfileImages", "supplier_profile"],
    ["galleryImages", "supplier_profile"],
    ["images", "supplier_profile"],
  ]) {
    const value = rawHotel[key];
    if (key === "hotelProfileImages" && value && typeof value === "object") {
      for (const row of urlsFromStructuredProfileImages(value.images, source)) {
        pushProfileImageUnique(ordered, row.url, row.source);
      }
      continue;
    }
    for (const row of urlsFromStructuredProfileImages(value, source)) {
      pushProfileImageUnique(ordered, row.url, row.source);
    }
  }
  return ordered;
}

function collectMainImageUrls(rawHotel) {
  const ordered = [];
  for (const key of [
    "MainImage",
    "mainImage",
    "mainImageUrl",
    "hotelImage",
    "imageUrl",
  ]) {
    pushProfileImageUnique(ordered, rawHotel[key], "main_image");
  }
  return ordered;
}

function collectSearchSnapshotUrls(searchSnapshot) {
  if (!searchSnapshot || typeof searchSnapshot !== "object") return [];
  const ordered = [];
  for (const key of [
    "image",
    "imageUrl",
    "thumbnail",
    "hotelImage",
    "mainImage",
    "DefaultImage",
  ]) {
    pushProfileImageUnique(ordered, searchSnapshot[key], "search_snapshot");
  }
  return ordered;
}

function collectDefaultImageUrls(rawHotel) {
  const ordered = [];
  pushProfileImageUnique(ordered, rawHotel.DefaultImage, "default_image");
  return ordered;
}

function resolveProfileAltName(rawHotel, searchSnapshot) {
  const candidates = [
    rawHotel.HotelName,
    rawHotel.DisplayName,
    searchSnapshot?.name,
    searchSnapshot?.DisplayName,
  ];
  for (const c of candidates) {
    if (typeof c === "string" && c.trim()) return c.trim();
  }
  return "";
}

/**
 * Hotel profile gallery SSOT — mirrors services/hotel_profile_images_ssot.py.
 */
export function normalizeHotelProfileImages(rawHotel, searchSnapshot, options = {}) {
  const hotel = rawHotel && typeof rawHotel === "object" ? rawHotel : {};
  const snapshot =
    searchSnapshot && typeof searchSnapshot === "object" ? searchSnapshot : null;

  const supplier = collectSupplierProfileUrls(hotel);
  const main = collectMainImageUrls(hotel);
  const search = collectSearchSnapshotUrls(snapshot);
  const defaultImg = collectDefaultImageUrls(hotel);
  const cached = [];
  for (const u of options.hotelImagesApiUrls || []) {
    pushProfileImageUnique(cached, u, "cached_photos");
  }

  const supplierImageCount = supplier.length;
  const merged = [];
  let primarySource = "none";

  for (const [group, sourceName] of [
    [supplier, "supplier_profile"],
    [main, "main_image"],
    [search, "search_snapshot"],
    [defaultImg, "default_image"],
    [cached, "cached_photos"],
  ]) {
    if (group.length > 0 && primarySource === "none") {
      primarySource = sourceName;
    }
    for (const row of group) {
      pushProfileImageUnique(merged, row.url, row.source);
    }
  }

  const alt = resolveProfileAltName(hotel, snapshot);
  const images = merged.map((row, idx) => ({
    url: row.url,
    alt,
    source: row.source,
    isPrimary: idx === 0,
  }));

  const mainImageUrl = images[0]?.url ?? null;
  const imageCount = images.length;
  const imageSource = imageCount > 0 ? primarySource : "none";

  return {
    images,
    mainImageUrl,
    imageCount,
    imageSource,
    trace: {
      supplierImageCount,
      normalizedImageCount: imageCount,
      firstImageUrl: mainImageUrl,
      imageSource,
    },
  };
}

/** Convenience: URL list for HotelHeroGallery */
export function hotelProfileGalleryUrlList(rawHotel, searchSnapshot, options = {}) {
  return normalizeHotelProfileImages(rawHotel, searchSnapshot, options).images.map(
    (row) => row.url
  );
}
